import { clockLabel } from '../calendar/calendar-layout'
import { minuteOf, type TimelineRange } from './age-scheduling-model'
import styles from './AgeSchedulingSettings.module.css'

function position(range: TimelineRange, minute: number) {
  const clamped = Math.min(Math.max(minute, range.startMinute), range.endMinute)
  return (
    ((clamped - range.startMinute) / (range.endMinute - range.startMinute)) *
    100
  )
}

/**
 * A day's bookable times as bars over the clinic's open hours. Decorative:
 * the same times are always written out beside it.
 */
export function AgeTimeline({
  windows,
  range,
  clinic,
  muted = false,
}: {
  windows: Array<{ start: string; end: string }>
  range: TimelineRange
  clinic: { start: string; end: string } | null
  muted?: boolean
}) {
  const bars = windows.filter(
    (window) => window.start && window.end && window.end > window.start
  )
  // Without opening hours to compare with, the whole day reads as open.
  const open = clinic
    ? {
        left: position(range, minuteOf(clinic.start)),
        right: position(range, minuteOf(clinic.end)),
      }
    : { left: 0, right: 100 }
  return (
    <div
      className={styles.track}
      aria-hidden="true"
      data-muted={muted || undefined}
    >
      <span
        className={styles.clinicHours}
        style={{ left: `${open.left}%`, width: `${open.right - open.left}%` }}
      />
      {range.ticks.slice(1, -1).map((tick) => (
        <span
          key={tick}
          className={styles.gridLine}
          style={{ left: `${position(range, tick)}%` }}
        />
      ))}
      {bars.map((window) => {
        const left = position(range, minuteOf(window.start))
        const right = position(range, minuteOf(window.end))
        return (
          <span
            key={`${window.start}-${window.end}`}
            className={styles.bar}
            style={{ left: `${left}%`, width: `${right - left}%` }}
          />
        )
      })}
    </div>
  )
}

/** Hour labels under (or over) a column of timelines. */
export function AgeTimelineAxis({ range }: { range: TimelineRange }) {
  return (
    <div className={styles.axis} aria-hidden="true">
      {range.ticks.map((tick, index) => (
        <span
          key={tick}
          data-edge={
            index === 0
              ? 'start'
              : index === range.ticks.length - 1 && tick === range.endMinute
                ? 'end'
                : undefined
          }
          style={{ left: `${position(range, tick)}%` }}
        >
          {clockLabel(tick)}
        </span>
      ))}
    </div>
  )
}
